import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Mail, RotateCcw, LogOut } from "lucide-react";
import { toast } from "sonner";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72034c2-4f95-4f18-be74-1d29b1871fdc/artifacts/djdyqu9t_IMG_1381.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CODE_LENGTH = 6;

export default function VerifyEmailPage() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(""));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const inputsRef = useRef([]);

  // Si ya está verificado, salir de esta pantalla
  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (user.email_verified) {
      navigate(user.has_paid ? "/app/feed" : "/payment", { replace: true });
    }
  }, [user, navigate]);

  // Al entrar en la pantalla (por login o registro), envía automáticamente un
  // nuevo código para que el usuario no tenga que pulsar "Reenviar". Solo se
  // ejecuta una vez por montaje.
  useEffect(() => {
    if (!user || user.email_verified) return;
    let cancelled = false;
    (async () => {
      try {
        await axios.post(`${API}/auth/send-verification-code`, {}, { withCredentials: true });
        if (!cancelled) setCooldown(60);
      } catch {
        // Silencio — si falla por rate limit u otro motivo, el usuario puede
        // pulsar "Reenviar" manualmente.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Cooldown para reenviar
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleChange = (idx, val) => {
    const clean = val.replace(/\D/g, "").slice(0, 1);
    const next = [...digits];
    next[idx] = clean;
    setDigits(next);
    setError("");
    if (clean && idx < CODE_LENGTH - 1) inputsRef.current[idx + 1]?.focus();
    // Auto-submit al completar
    if (clean && idx === CODE_LENGTH - 1 && next.every((d) => d)) {
      submitCode(next.join(""));
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && idx > 0) inputsRef.current[idx - 1]?.focus();
    if (e.key === "ArrowRight" && idx < CODE_LENGTH - 1) inputsRef.current[idx + 1]?.focus();
  };

  const handlePaste = (e) => {
    const text = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, CODE_LENGTH);
    if (!text) return;
    e.preventDefault();
    const next = text.split("").concat(Array(CODE_LENGTH).fill("")).slice(0, CODE_LENGTH);
    setDigits(next);
    if (next.every((d) => d)) submitCode(next.join(""));
    else inputsRef.current[Math.min(text.length, CODE_LENGTH - 1)]?.focus();
  };

  const submitCode = async (code) => {
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/auth/verify-email-code`, { code }, { withCredentials: true });
      toast.success("Email verificado correctamente");
      await refreshUser();
      // navegación se hará vía useEffect al detectar email_verified
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      setDigits(Array(CODE_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError("");
    try {
      await axios.post(`${API}/auth/send-verification-code`, {}, { withCredentials: true });
      toast.success("Código reenviado. Revisa tu bandeja de entrada.");
      setCooldown(60);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F5EEDB] flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-md animate-fade-in">
        <div className="text-center mb-6">
          <img src={LOGO_URL} alt="Refugium" className="w-14 h-14 rounded-full mx-auto mb-3" />
          <h1 className="font-heading text-3xl text-[#5B3E2E]">Verifica tu email</h1>
        </div>

        <div className="bg-[#FDFAF4] rounded-3xl p-6 sm:p-8 border border-[#5B3E2E]/10 shadow-[0_8px_30px_rgba(91,62,46,0.06)]">
          <div className="flex items-start gap-3 mb-5 p-3 bg-[#E48D57]/10 rounded-xl">
            <Mail size={18} className="text-[#E48D57] mt-0.5 flex-shrink-0" />
            <p className="text-sm text-[#5B3E2E] leading-relaxed">
              Hemos enviado un código de 6 dígitos a{" "}
              <strong className="break-all">{user?.email}</strong>. Introdúcelo abajo para continuar.
            </p>
          </div>

          {error && (
            <div data-testid="verify-error" className="bg-[#D9534F]/10 text-[#D9534F] text-sm rounded-xl p-3 mb-4">
              {error}
            </div>
          )}

          <div className="flex justify-center gap-2 sm:gap-3 mb-6" onPaste={handlePaste}>
            {digits.map((d, idx) => (
              <input
                key={idx}
                ref={(el) => (inputsRef.current[idx] = el)}
                data-testid={`verify-digit-${idx}`}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                value={d}
                onChange={(e) => handleChange(idx, e.target.value)}
                onKeyDown={(e) => handleKeyDown(idx, e)}
                disabled={submitting}
                className="w-11 h-14 sm:w-12 sm:h-16 text-center text-2xl font-heading text-[#5B3E2E] bg-transparent border-2 border-[#5B3E2E]/20 rounded-xl focus:border-[#E48D57] focus:ring-0 outline-none transition-colors disabled:opacity-50"
              />
            ))}
          </div>

          <Button
            data-testid="verify-submit"
            onClick={() => submitCode(digits.join(""))}
            disabled={submitting || digits.some((d) => !d)}
            className="w-full bg-[#E48D57] hover:bg-[#D07C46] text-white rounded-full py-3 font-medium transition-all disabled:opacity-40"
          >
            {submitting ? "Verificando..." : "Verificar"}
          </Button>

          <div className="mt-5 pt-5 border-t border-[#5B3E2E]/10 text-center">
            <p className="text-xs text-[#5B3E2E]/60 mb-2">¿No has recibido el correo?</p>
            <button
              data-testid="verify-resend"
              type="button"
              onClick={handleResend}
              disabled={cooldown > 0}
              className="inline-flex items-center gap-2 text-sm text-[#E48D57] hover:text-[#D07C46] font-medium disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RotateCcw size={14} />
              {cooldown > 0 ? `Reenviar en ${cooldown}s` : "Reenviar código"}
            </button>
          </div>
        </div>

        <div className="mt-5 text-center">
          <button
            data-testid="verify-logout"
            onClick={handleLogout}
            className="inline-flex items-center gap-1.5 text-xs text-[#5B3E2E]/50 hover:text-[#5B3E2E]/80"
          >
            <LogOut size={12} />
            Cerrar sesión y usar otra cuenta
          </button>
        </div>
      </div>
    </div>
  );
}
